/**
 * SpatialBroadcastPlayer.tsx
 *
 * A compact, audio-first player for a spatial object's live broadcast —
 * a building talking to whoever is standing near it.
 *
 * It is the existing viewer, not a new one. `joinBroadcast` connects to
 * the same `webrtcHub` room an agent broadcast uses, and the same
 * `AgentBroadcastEngine` decodes and schedules the caption audio. The
 * only real differences are cosmetic and deliberate:
 *
 *   - The engine's canvas is shrunk to a token size and slowed to a
 *     crawl. A spatial broadcast is audio; rendering a 1280×720 avatar
 *     at 30 fps to feed an `<audio>` element would burn a phone battery
 *     to produce pixels nobody sees.
 *   - The output binds to `<audio>` rather than `<video>`, so the player
 *     stays a strip rather than a frame.
 *
 * Autoplay policy is respected rather than worked around. The player
 * connects immediately so the first utterance is not missed, but audio
 * stays muted until a real user gesture; when configuration asks for
 * autoplay we try once and fall back to the Listen button the moment the
 * browser refuses.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  joinBroadcast,
  type ViewerController,
  type AgentCaptionEvent,
} from '../../services/webrtcBroadcastService';
import {
  createAgentBroadcastEngine,
  type AgentBroadcastEngine,
} from '../AgentBroadcastCanvas';

export interface SpatialBroadcastPlayerProps {
  spatialObjectId: string;
  /** Room key. `spatial:<uuid>`, as minted by spatialBroadcastService. */
  broadcasterId: string;
  objectName: string | null;
  title?: string | null;
  /** Metres to the object's broadcast anchor. Shown, not enforced. */
  distanceMeters?: number | null;
  /** Distance-derived gain in 0..1, from the discovery response. */
  volume?: number;
  /** Configuration's wish. The browser still has the final say. */
  autoPlayAudio?: boolean;
  viewerWallet?: string | null;
  country?: number | null;
  onEnded?: (reason: string) => void;
  onClose?: () => void;
}

const STYLE_ID = 'gt-spatial-player-styles';

function installStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    .gt-sbp {
      display: flex; flex-direction: column; gap: 6px;
      padding: 10px 12px; border-radius: 12px;
      background: rgba(14, 16, 22, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.10);
      color: #e9edf5; font-size: 12px; line-height: 1.35;
      max-width: 320px;
    }
    .gt-sbp__head { display: flex; align-items: center; gap: 8px; }
    .gt-sbp__live {
      display: inline-flex; align-items: center; gap: 5px;
      font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; color: #ff5470;
    }
    .gt-sbp__dot {
      width: 7px; height: 7px; border-radius: 50%;
      background: #ff5470; animation: gt-sbp-pulse 1.6s ease-in-out infinite;
    }
    @keyframes gt-sbp-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
    .gt-sbp__name { font-weight: 600; flex: 1 1 auto; min-width: 0;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .gt-sbp__meta { color: rgba(233, 237, 245, 0.6); font-size: 11px; }
    .gt-sbp__caption {
      min-height: 2.4em; color: rgba(233, 237, 245, 0.92);
      display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .gt-sbp__caption--idle { color: rgba(233, 237, 245, 0.45); font-style: italic; }
    .gt-sbp__actions { display: flex; align-items: center; gap: 6px; }
    .gt-sbp__btn {
      appearance: none; border: 1px solid rgba(255,255,255,0.16);
      background: rgba(255,255,255,0.06); color: #e9edf5;
      border-radius: 8px; padding: 5px 10px; font-size: 11px; font-weight: 600;
      cursor: pointer;
    }
    .gt-sbp__btn:hover { background: rgba(255,255,255,0.12); }
    .gt-sbp__btn--primary { background: #ff5470; border-color: #ff5470; color: #10121a; }
    .gt-sbp__btn--primary:hover { background: #ff6c84; }
    .gt-sbp__status { color: rgba(233,237,245,0.55); font-size: 11px; }
    @media (max-width: 480px) { .gt-sbp { max-width: none; width: 100%; } }
  `;
  document.head.appendChild(s);
}

/** Caption audio arrives base64-encoded; the engine wants bytes. */
function captionAudioBlob(caption: AgentCaptionEvent): Blob | null {
  if (!caption.audio_base64) return null;
  try {
    const bin = atob(caption.audio_base64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
    return new Blob([buf], { type: caption.mime || 'audio/mpeg' });
  } catch {
    return null;
  }
}

export const SpatialBroadcastPlayer: React.FC<SpatialBroadcastPlayerProps> = ({
  spatialObjectId,
  broadcasterId,
  objectName,
  title = null,
  distanceMeters = null,
  volume = 1,
  autoPlayAudio = false,
  viewerWallet = null,
  country = null,
  onEnded,
  onClose,
}) => {
  installStyles();

  const viewerRef = useRef<ViewerController | null>(null);
  const engineRef = useRef<AgentBroadcastEngine | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingRef = useRef<AgentCaptionEvent[]>([]);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(true);
  const [caption, setCaption] = useState<string>('');
  const [status, setStatus] = useState<'connecting' | 'listening' | 'ended'>('connecting');
  const [endedReason, setEndedReason] = useState<string | null>(null);

  const pushToEngine = useCallback(
    (engine: AgentBroadcastEngine, event: AgentCaptionEvent) => {
      const text = (event.text || '').trim();
      if (!text) return;
      setCaption(text);
      engine
        .pushCaption({
          text,
          audio: captionAudioBlob(event),
          durationMs: event.duration_ms,
        })
        .catch(() => { /* text still rendered; a failed decode is not fatal */ });
    },
    [],
  );

  // ── Join ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!broadcasterId) return undefined;
    setEndedReason(null);
    setStatus('connecting');
    pendingRef.current = [];

    const viewer = joinBroadcast({
      broadcasterId,
      wallet: viewerWallet || undefined,
      country: country == null ? undefined : Number(country),
      onStream: (s) => setStream(s),
      onMode: (mode) => {
        if (mode !== 'signaling_only') { setStatus('listening'); return; }
        if (engineRef.current) return;

        // A token canvas at one frame per second. The engine's audio
        // graph is what we are here for; its renderer is overhead.
        const engine = createAgentBroadcastEngine({
          agentPublicKey: broadcasterId,
          displayName: objectName,
          avatarUrl: null,
          width: 64,
          height: 64,
          fps: 1,
        });
        engineRef.current = engine;
        try { setStream(engine.getStream()); } catch { /* captions still arrive */ }
        setStatus('listening');

        for (const buffered of pendingRef.current.splice(0)) pushToEngine(engine, buffered);
      },
      onAgentCaption: (event) => {
        // Ignore anything from a different object sharing the socket.
        if (event.spatialObjectId && event.spatialObjectId !== spatialObjectId) return;
        const engine = engineRef.current;
        if (!engine) {
          const buffer = pendingRef.current;
          buffer.push(event);
          while (buffer.length > 5) buffer.shift();
          return;
        }
        pushToEngine(engine, event);
      },
      onCaptionCancel: (cancel) => {
        if (cancel.spatialObjectId && cancel.spatialObjectId !== spatialObjectId) return;
        // The superseded audio cannot be recalled from the engine's
        // schedule, but the stale text should not sit under a new line.
        setCaption('');
      },
      onEnded: (reason) => {
        setStatus('ended');
        setEndedReason(reason || 'ended');
        try { onEnded?.(reason || 'ended'); } catch { /* ignore */ }
      },
      onDenied: (reason) => {
        setStatus('ended');
        setEndedReason(reason || 'denied');
        try { onEnded?.(reason); } catch { /* ignore */ }
      },
      onError: () => { /* transient; the viewer reconnects on its own */ },
      shouldResume: () => true,
    });
    viewerRef.current = viewer;

    return () => {
      try { viewerRef.current?.stop(); } catch { /* ignore */ }
      viewerRef.current = null;
      try { engineRef.current?.dispose(); } catch { /* ignore */ }
      engineRef.current = null;
    };
  }, [broadcasterId, spatialObjectId, objectName, viewerWallet, country, pushToEngine, onEnded]);

  // ── Bind the stream to the audio element ─────────────────────────────
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !stream) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    el.muted = muted;
    el.volume = Math.max(0, Math.min(1, volume));
    void el.play().catch(() => { /* muted playback may still be blocked */ });
  }, [stream, muted, volume]);

  // Distance changes as the listener moves; the gain follows without a
  // reconnect, which is the point of computing it server-side.
  useEffect(() => {
    const el = audioRef.current;
    if (el) el.volume = Math.max(0, Math.min(1, volume));
  }, [volume]);

  const listen = useCallback(() => {
    setMuted(false);
    const el = audioRef.current;
    if (el) {
      try { el.muted = false; } catch { /* ignore */ }
      void el.play().catch(() => { /* the user can tap again */ });
    }
    // Must happen inside the same gesture: the AudioContext resume is
    // what actually makes scheduled TTS audible on iOS Safari.
    engineRef.current?.unlockAudio().catch(() => { /* re-tap works */ });
  }, []);

  // ── Configured autoplay, attempted honestly ──────────────────────────
  useEffect(() => {
    if (!autoPlayAudio || !stream || !muted) return;
    const el = audioRef.current;
    if (!el) return;
    let cancelled = false;
    el.muted = false;
    el.play().then(
      () => { if (!cancelled) setMuted(false); },
      () => {
        // Refused for want of a gesture. Put the mute back and leave the
        // Listen button to do its job.
        try { el.muted = true; } catch { /* ignore */ }
      },
    );
    return () => { cancelled = true; };
  }, [autoPlayAudio, stream, muted]);

  const heading = title || `${objectName || 'Spatial object'} live`;

  return (
    <div className="gt-sbp" data-object-id={spatialObjectId}>
      <div className="gt-sbp__head">
        <span className="gt-sbp__live">
          <span className="gt-sbp__dot" />
          Live
        </span>
        <span className="gt-sbp__name" title={heading}>{heading}</span>
        {onClose && (
          <button type="button" className="gt-sbp__btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>

      {distanceMeters != null && (
        <div className="gt-sbp__meta">
          {distanceMeters < 1000
            ? `${Math.round(distanceMeters)} m away`
            : `${(distanceMeters / 1000).toFixed(1)} km away`}
        </div>
      )}

      <div className={`gt-sbp__caption${caption ? '' : ' gt-sbp__caption--idle'}`}>
        {caption || (status === 'ended'
          ? `Broadcast ended${endedReason ? ` (${endedReason})` : ''}`
          : 'Waiting for the next thing it has to say…')}
      </div>

      <div className="gt-sbp__actions">
        {muted ? (
          <button type="button" className="gt-sbp__btn gt-sbp__btn--primary" onClick={listen}>
            Listen
          </button>
        ) : (
          <button type="button" className="gt-sbp__btn" onClick={() => setMuted(true)}>
            Mute
          </button>
        )}
        <span className="gt-sbp__status">
          {status === 'connecting' && 'Connecting…'}
          {status === 'listening' && (muted ? 'Muted' : 'Playing')}
          {status === 'ended' && 'Ended'}
        </span>
      </div>

      {/* Audio-only by design: the stream carries a token video track the
          engine needs for its capture graph, and an <audio> element
          simply ignores it. */}
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
    </div>
  );
};

export default SpatialBroadcastPlayer;
